import { VideoEditor } from '../VideoEditor';
import { Command } from '../commands/Command';
import { EditorChangeType } from '../types';

/**
 * CommandManager is responsible for executing commands, managing command history,
 * and providing undo/redo functionality for the VideoEditor.
 */
export class CommandManager {
  private commandHistory: Command[] = [];
  private commandIndex: number = -1;

  /**
   * Creates a new CommandManager instance
   * @param editor The VideoEditor instance this manager coordinates with
   */
  constructor(private editor: VideoEditor) {}

  /**
   * Gets the last executed command or null if history is empty
   * @returns The last executed command or null
   */
  public getLastExecutedCommand(): Command | null {
    if (this.commandIndex < 0) {
      return null;
    }
    return this.commandHistory[this.commandIndex];
  }

  /**
   * Gets the next command in the history that can be redone
   * @returns The next command to redo or null if there's nothing to redo
   */
  public getNextUndoneCommand(): Command | null {
    if (this.commandIndex >= this.commandHistory.length - 1) {
      return null;
    }
    return this.commandHistory[this.commandIndex + 1];
  }

  /**
   * Executes a command and adds it to history
   * Automatically wraps the command execution in a batch operation for better performance
   * @param command The command to execute
   * @returns true if the command execution was successful, false otherwise
   */
  public execute(command: Command): boolean {
    // Use executeInBatch to wrap the command execution
    return this.editor.executeInBatch(() => {
      // Execute the command
      const success = command.execute();

      if (success) {
        // If we're in the middle of the history, clear the future commands
        if (this.commandIndex < this.commandHistory.length - 1) {
          this.commandHistory = this.commandHistory.slice(
            0,
            this.commandIndex + 1,
          );
        }

        // Add the command to history
        this.commandHistory.push(command);
        this.commandIndex = this.commandHistory.length - 1;

        // Log the command execution
        console.log(`Executed command: ${command.getDescription()}`);

        // Mark timeline as dirty after successful command execution
        this.editor.markDirty(`command: ${command.getDescription()}`);
      }

      return success;
    });
  }

  /**
   * Undoes the last command
   * @returns true if the undo was successful, false if there's nothing to undo
   */
  public undo(): boolean {
    if (this.commandIndex < 0) {
      console.log('Nothing to undo');
      return false;
    }

    const command = this.commandHistory[this.commandIndex];
    console.log(`Undoing: ${command.getDescription()}`);

    // Use executeInBatch to wrap the undo operation
    return this.editor.executeInBatch(() => {
      const success = command.undo();

      if (success) {
        this.commandIndex--;
        console.log(`Undo successful, command index now: ${this.commandIndex}`);

        // Mark timeline as dirty
        this.editor.markDirty(`undo: ${command.getDescription()}`);

        // Notify about command undo
        this.editor.notifyObservers(EditorChangeType.COMMAND_UNDONE, {
          command: command,
          description: command.getDescription(),
          historyIndex: this.commandIndex,
        });
      } else {
        console.warn('Failed to undo command');
      }

      return success;
    });
  }

  /**
   * Redoes a previously undone command
   * @returns true if the redo was successful, false if there's nothing to redo
   */
  public redo(): boolean {
    if (this.commandIndex >= this.commandHistory.length - 1) {
      console.log('Nothing to redo');
      return false;
    }

    const command = this.commandHistory[this.commandIndex + 1];
    console.log(`Redoing: ${command.getDescription()}`);

    // Use executeInBatch to wrap the redo operation
    return this.editor.executeInBatch(() => {
      const success = command.execute();

      if (success) {
        this.commandIndex++;
        console.log(`Redo successful, command index now: ${this.commandIndex}`);

        // Mark timeline as dirty
        this.editor.markDirty(`redo: ${command.getDescription()}`);

        // Notify about command redo
        this.editor.notifyObservers(EditorChangeType.COMMAND_REDONE, {
          command: command,
          description: command.getDescription(),
          historyIndex: this.commandIndex,
        });
      } else {
        console.warn('Failed to redo command');
      }

      return success;
    });
  }

  /**
   * Clears the command history. This is useful when an external system
   * (e.g., VideoCreatorStore) needs to reset its own undo/redo stack and
   * wants the editor's internal history to stay in sync.
   */
  public clearHistory(): void {
    this.commandHistory = [];
    this.commandIndex = -1;
  }
}
